// BULKVENDOR1 + BULKEDIT1 (docs/build-briefs/BULKEDIT1-inventory-bulk-edit.md). The pure part of Settings > Inventory > Items bulk
// edit: the sentences the person reads for each of the four actions, the batches the screen sends, and the running total of the
// answers. No DOM, no calls.

import type { BulkSetVendorFailureDto } from "./api.ts";

/** Items sent per request. The server accepts more; smaller batches keep each call short and let the sheet show progress. */
export const BULK_EDIT_BATCH_SIZE = 100;

export type BulkEditKind = "vendor" | "shelfTier" | "active" | "stock86";

export interface BulkEditOption {
  /** What the screen sends: a vendor id, a shelf tier key, "true" or "false". */
  value: string;
  label: string;
}

/** The words of one action's sheet. */
export interface BulkEditSpec {
  kind: BulkEditKind;
  /** The button on the action strip. */
  stripLabel: string;
  title: string;
  /** The accessible name of the radio list. */
  groupLabel: string;
  pickPrompt: string;
  noOptions: string;
  loadingOptions: string;
  loadFailed: string;
  /** An optional line under the confirmation sentence. */
  note: string;
  applying: string;
  /** The sentence the person confirms. */
  confirm: (option: BulkEditOption, itemCount: number, locationName: string | undefined) => string;
  /** The primary button once an option is picked. */
  apply: (option: BulkEditOption, itemCount: number) => string;
  /** The primary button before an option is picked. */
  applyIdle: string;
  /** The "Press X again" words after a stopped run. */
  again: string;
}

export const BULK_EDIT_COPY = {
  clear: "Clear",
  cancel: "Cancel",
  done: "Done",
  tryAgain: "Try again",
  selected: "selected",
} as const;

function itemsWord(count: number): string {
  return count === 1 ? "1 item" : `${count} items`;
}

function whereWord(locationName: string | undefined): string {
  return locationName === undefined || locationName === "" ? "this location" : locationName;
}

export const BULK_EDIT_SPECS: Record<BulkEditKind, BulkEditSpec> = {
  vendor: {
    kind: "vendor",
    stripLabel: "Vendor",
    title: "Set vendor",
    groupLabel: "Vendor",
    pickPrompt: "Pick a vendor to continue.",
    noOptions: "No active vendors yet. Add a vendor in Settings, Purchasing first.",
    loadingOptions: "Loading vendors",
    loadFailed: "Could not load the vendors.",
    note: "An item with no offer from this vendor gets one with no price. Add the prices later.",
    applying: "Setting vendor",
    confirm: (option, count, place) => `Set ${option.label} as the vendor for ${itemsWord(count)} at ${whereWord(place)}.`,
    apply: (option, count) => `Set ${option.label} for ${itemsWord(count)}`,
    applyIdle: "Set vendor",
    again: "Set vendor",
  },
  shelfTier: {
    kind: "shelfTier",
    stripLabel: "Shelf tier",
    title: "Set shelf tier",
    groupLabel: "Shelf tier",
    pickPrompt: "Pick a shelf tier to continue.",
    noOptions: "No shelf tiers yet. Add them in Settings, Location settings first.",
    loadingOptions: "Loading shelf tiers",
    loadFailed: "Could not load the shelf tiers.",
    note: "",
    applying: "Setting shelf tier",
    confirm: (option, count) => `Set the shelf tier of ${itemsWord(count)} to ${option.label}.`,
    apply: (option, count) => `Set ${option.label} for ${itemsWord(count)}`,
    applyIdle: "Set shelf tier",
    again: "Set shelf tier",
  },
  active: {
    kind: "active",
    stripLabel: "Active / Inactive",
    title: "Active or inactive",
    groupLabel: "Active or inactive",
    pickPrompt: "Pick Active or Inactive to continue.",
    noOptions: "",
    loadingOptions: "",
    loadFailed: "",
    note: "An inactive item stays in the catalog and its history. It is not offered for sale.",
    applying: "Saving",
    confirm: (option, count) => `Make ${itemsWord(count)} ${option.label.toLowerCase()}.`,
    apply: (option, count) => `Make ${itemsWord(count)} ${option.label.toLowerCase()}`,
    applyIdle: "Apply",
    again: "Apply",
  },
  stock86: {
    kind: "stock86",
    stripLabel: "86 / Back in stock",
    title: "86 or back in stock",
    groupLabel: "86 or back in stock",
    pickPrompt: "Pick 86'd or Back in stock to continue.",
    noOptions: "",
    loadingOptions: "",
    loadFailed: "",
    note: "86 never changes On hand.",
    applying: "Saving",
    confirm: (option, count, place) =>
      option.value === "true"
        ? `Mark ${itemsWord(count)} as 86'd at ${whereWord(place)}.`
        : `Put ${itemsWord(count)} back in stock at ${whereWord(place)}.`,
    apply: (option, count) => (option.value === "true" ? `86 ${itemsWord(count)}` : `Put ${itemsWord(count)} back in stock`),
    applyIdle: "Apply",
    again: "Apply",
  },
};

/** The two fixed choices of Active / Inactive and of 86 / Back in stock. */
export const BULK_EDIT_FIXED_OPTIONS: Partial<Record<BulkEditKind, readonly BulkEditOption[]>> = {
  active: [
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ],
  stock86: [
    { value: "true", label: "86'd" },
    { value: "false", label: "Back in stock" },
  ],
};

/** "3 selected". */
export function selectedLabel(count: number): string {
  return `${count} ${BULK_EDIT_COPY.selected}`;
}

/** "Select all 41 shown". */
export function selectAllLabel(shownCount: number): string {
  return `Select all ${shownCount} shown`;
}

/** Splits the ids into batches of at most `size`, in order. */
export function toBatches(ids: readonly string[], size: number = BULK_EDIT_BATCH_SIZE): string[][] {
  const out: string[][] = [];
  for (let at = 0; at < ids.length; at += size) {
    out.push(ids.slice(at, at + size));
  }
  return out;
}

export interface BulkEditTotals {
  changed: number;
  alreadySet: number;
  offersAdded: number;
  failed: BulkSetVendorFailureDto[];
}

/** One batch's answer, from either bulk route (only the vendor route has `offersAdded`). */
export interface BulkEditBatchResult {
  changed: number;
  alreadySet: number;
  offersAdded?: number;
  failed: BulkSetVendorFailureDto[];
}

export function emptyTotals(): BulkEditTotals {
  return { changed: 0, alreadySet: 0, offersAdded: 0, failed: [] };
}

/** Adds one batch's answer to the running total. */
export function addBatch(totals: BulkEditTotals, batch: BulkEditBatchResult): BulkEditTotals {
  return {
    changed: totals.changed + batch.changed,
    alreadySet: totals.alreadySet + batch.alreadySet,
    offersAdded: totals.offersAdded + (batch.offersAdded ?? 0),
    failed: [...totals.failed, ...batch.failed],
  };
}

/** "214 changed, 12 already set, 3 could not be changed." Every part is always said, so a zero is visible. */
export function resultSentence(totals: BulkEditTotals): string {
  return `${totals.changed} changed, ${totals.alreadySet} already set, ${totals.failed.length} could not be changed.`;
}

/** The second line after a vendor result that added offers, or an empty string. */
export function offersAddedSentence(totals: BulkEditTotals): string {
  if (totals.offersAdded === 0) return "";
  return `${itemsWord(totals.offersAdded)} had no offer from this vendor, so each got one with no price.`;
}

/** The line when the run stopped early: what was done, and that running it again skips finished items. */
export function stoppedSentence(doneCount: number, totalCount: number, message: string, again: string): string {
  return `Stopped after ${doneCount} of ${totalCount} items. ${message} Press ${again} again to continue; items already set are skipped.`;
}
